import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Search, 
  Filter, 
  CheckCircle2, 
  XCircle, 
  Users, 
  MapPin, 
  GraduationCap,
  Clock,
  QrCode,
  List,
  Eye,
  History,
  Info,
  Truck,
  User,
  X
} from 'lucide-react';
import { Card, Button, Input, Select, Badge } from '../../components/ui/index.jsx';
import { useGet } from '../../hooks/useApi';
import api from '../../utils/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const LogsModal = ({ student, onClose }) => {
    if (!student) return null;

    // Sorting attendances from oldest to newest (ascending)
    const sortedLogs = [...(student.attendances || [])].sort((a, b) => 
        new Date(a.scanned_at) - new Date(b.scanned_at)
    );

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-white w-full max-w-md rounded-2xl shadow-xl overflow-hidden animate-in zoom-in-95 duration-300 border border-slate-100 font-sans">
                {/* Modal Header */}
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 text-primary rounded-xl flex items-center justify-center font-bold text-lg shadow-sm">
                            {student.full_name.charAt(0)}
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-slate-800">{student.full_name}</h3>
                            <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">{student.school_class?.name}</p>
                        </div>
                    </div>
                    <button 
                        onClick={onClose} 
                        className="w-8 h-8 rounded-lg hover:bg-slate-50 text-slate-400 hover:text-slate-600 flex items-center justify-center transition-all duration-200 border border-transparent hover:border-slate-100"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="px-6 py-6 space-y-6 max-h-[60vh] overflow-y-auto custom-scrollbar">
                    {/* Bus & Driver Summary */}
                    <div className="grid grid-cols-2 gap-3 pb-4 border-b border-slate-50">
                        <div className="flex flex-col">
                            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mb-1">الحافلة</p>
                            <div className="flex items-center gap-2">
                                <Truck size={14} className="text-primary/60" />
                                <span className="text-xs font-semibold text-slate-700">{student.bus?.matricule || '---'}</span>
                            </div>
                        </div>
                        <div className="flex flex-col border-r border-slate-50 pr-3">
                            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mb-1">السائق</p>
                            <div className="flex items-center gap-2">
                                <User size={14} className="text-primary/60" />
                                <span className="text-xs font-semibold text-slate-700 truncate">{student.bus?.driver?.full_name || '---'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Timeline */}
                    <div className="space-y-4">
                        <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-6">
                            تاريخ الحركات اليومي
                        </h4>

                        {sortedLogs.length > 0 ? (
                            <div className="space-y-0 relative pr-4">
                                {/* Dashed Line (RTL: right side) */}
                                <div className="absolute right-0 top-2 bottom-2 w-0.5 border-r-2 border-dashed border-slate-200"></div>

                                {sortedLogs.map((log, idx) => (
                                    <div key={log.id} className="relative pr-8 pb-8 last:pb-0">
                                        {/* Timeline Dot */}
                                        <div className="absolute right-[-4px] top-1.5 w-2.5 h-2.5 rounded-full bg-white border-2 border-primary z-10"></div>
                                        
                                        <div className="flex flex-col bg-slate-50/50 p-3 rounded-xl border border-slate-100/50">
                                            <div className="flex items-center justify-between">
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">حركة #{idx + 1}</span>
                                                <div className="flex items-center gap-1.5 text-sm font-bold text-slate-900 tabular-nums">
                                                    <Clock size={12} className="text-primary/60" />
                                                    {new Date(log.scanned_at).toLocaleTimeString('en-GB', { 
                                                        hour: '2-digit', 
                                                        minute: '2-digit',
                                                        hour12: false 
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-10 opacity-30">
                                <History size={32} className="text-slate-300 mb-2" />
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">لا توجد حركات</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="px-6 py-5 bg-slate-50/50 border-t border-slate-100">
                    <Button onClick={onClose} className="w-full h-11 rounded-xl text-sm font-bold shadow-sm">إغلاق</Button>
                </div>
            </div>
        </div>
    );
};

const AttendanceList = () => {
    const navigate = useNavigate();
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedClass, setSelectedClass] = useState('');
    const [selectedGroup, setSelectedGroup] = useState('');
    const [selectedRegion, setSelectedRegion] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedStudentLogs, setSelectedStudentLogs] = useState(null);

    // Fetch data
    const { data: attendanceResponse, loading, refresh } = useGet(
        `/attendance?page=${currentPage}&date=${date}&search=${searchTerm}&school_class_id=${selectedClass}&group_id=${selectedGroup}&region_id=${selectedRegion}`
    );
    const { hasPermission } = useAuth();
    const canCreate = hasPermission('attendance', 'create');

    const { data: regionsRaw } = useGet('/regions?all=true');
    const { data: schoolClassesRaw } = useGet('/school-classes?all=true');
    const { data: groupsRaw } = useGet('/groups?all=true');
    const { data: stats } = useGet('/attendance/stats');

    const students = attendanceResponse?.data || [];
    const pagination = attendanceResponse?.meta || null;
    const regions = Array.isArray(regionsRaw) ? regionsRaw : (regionsRaw?.data || []);
    const schoolClasses = Array.isArray(schoolClassesRaw) ? schoolClassesRaw : (schoolClassesRaw?.data || []);
    const groups = Array.isArray(groupsRaw) ? groupsRaw : (groupsRaw?.data || []);

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl text-slate-800 font-normal">سجل الحضور والغياب</h2>
                    <p className="text-slate-400 text-[11px] mt-1 font-normal uppercase tracking-wider">نظام مراقبة التواقيت والطوابع الزمنية</p>
                </div>
                {canCreate && (
                    <Button onClick={() => navigate('/dashboard/attendance/scanner')} className="bg-primary text-white shadow-lg shadow-primary/20">
                        <QrCode className="w-4 h-4 ml-2" />
                        فتح الماسح الضوئي
                    </Button>
                )}
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-4 flex items-center gap-4 bg-white border-slate-100">
                    <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center">
                        <CheckCircle2 size={24} />
                    </div>
                    <div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">حاضرون اليوم</p>
                        <p className="text-xl font-black text-slate-800">{stats?.present || 0}</p>
                    </div>
                </Card>
                <Card className="p-4 flex items-center gap-4 bg-white border-slate-100">
                    <div className="w-12 h-12 bg-rose-50 text-rose-600 rounded-2xl flex items-center justify-center">
                        <XCircle size={24} />
                    </div>
                    <div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">غائبون اليوم</p>
                        <p className="text-xl font-black text-slate-800">{stats?.absent || 0}</p>
                    </div>
                </Card>
                <Card className="p-4 flex items-center gap-4 bg-white border-slate-100">
                    <div className="w-12 h-12 bg-slate-50 text-slate-400 rounded-2xl flex items-center justify-center">
                        <Users size={24} />
                    </div>
                    <div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">إجمالي التلاميذ</p>
                        <p className="text-xl font-black text-slate-800">{stats?.total_active || 0}</p>
                    </div>
                </Card>
            </div>

            {/* Filters */}
            <Card className="p-4 bg-white border-slate-100">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div className="space-y-1">
                        <label className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mr-1 text-right block">تاريخ السجل</label>
                        <input 
                            type="date" 
                            className="w-full bg-slate-50 border border-slate-100 rounded-xl p-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-semibold text-slate-700"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mr-1 text-right block">بحث بالتلميذ</label>
                        <Input 
                            placeholder="الاسم أو رقم مسار..." 
                            icon={Search} 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="bg-slate-50 border-slate-100 rounded-xl"
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mr-1 text-right block">القسم</label>
                        <Select 
                            options={[
                                { value: '', label: 'كل الأقسام' },
                                ...schoolClasses.map(sc => ({ value: sc.id, label: sc.name }))
                            ]}
                            value={selectedClass}
                            onChange={(e) => setSelectedClass(e.target.value)}
                            className="bg-slate-50 border-slate-100 rounded-xl text-right"
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mr-1 text-right block">المجموعة</label>
                        <Select 
                            options={[
                                { value: '', label: 'كل المجموعات' },
                                ...groups.map(g => ({ value: g.id, label: g.name }))
                            ]}
                            value={selectedGroup}
                            onChange={(e) => setSelectedGroup(e.target.value)}
                            className="bg-slate-50 border-slate-100 rounded-xl text-right"
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mr-1 text-right block">المنطقة</label>
                        <Select 
                            options={[
                                { value: '', label: 'كل المناطق' },
                                ...regions.map(r => ({ value: r.id, label: r.region_name }))
                            ]}
                            value={selectedRegion}
                            onChange={(e) => setSelectedRegion(e.target.value)}
                            className="bg-slate-50 border-slate-100 rounded-xl text-right"
                        />
                    </div>
                </div>
            </Card>

            {/* Table */}
            <Card className="overflow-hidden border-slate-100 bg-white rounded-2xl shadow-sm border-none">
                <div className="overflow-x-auto">
                    <table className="w-full text-right border-collapse">
                        <thead>
                            <tr className="bg-slate-50/50 border-b border-slate-50">
                                <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">معلومات التلميذ</th>
                                <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">الحالة</th>
                                <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">مرات المسح</th>
                                <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">آخر حركة</th>
                                <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">التفاصيل</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                Array(5).fill(0).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan="5" className="px-6 py-8 text-center"><div className="h-4 bg-slate-50 rounded-full w-full"></div></td>
                                    </tr>
                                ))
                            ) : students.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-16 text-center text-slate-400 font-semibold">
                                        لا توجد بيانات حضور في هذا التاريخ
                                    </td>
                                </tr>
                            ) : (
                                students.map(student => {
                                    const isPresent = student.attendances?.length > 0;
                                    const lastLog = isPresent ? student.attendances[student.attendances.length - 1] : null;

                                    return (
                                        <tr key={student.id} className="hover:bg-slate-50/50 transition-all border-none">
                                            <td className="px-6 py-4 min-w-[220px]">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-400 flex items-center justify-center font-bold shadow-inner border border-slate-200/50">
                                                        {student.full_name.charAt(0)}
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-semibold text-slate-800 leading-tight">{student.full_name}</p>
                                                        <div className="flex items-center gap-2 mt-0.5">
                                                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">{student.school_class?.name || '---'}</p>
                                                            <span className="w-1 h-1 rounded-full bg-slate-200"></span>
                                                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">{student.group?.name || '---'}</p>
                                                            <span className="w-1 h-1 rounded-full bg-slate-200"></span>
                                                            <p className="text-[10px] text-primary/60 font-bold uppercase tracking-tight">{student.code_massar}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex justify-center">
                                                    {isPresent ? (
                                                        <Badge variant="success" className="bg-emerald-50 text-emerald-600 border-none font-bold text-[9px] px-3 py-1 flex items-center gap-1.5 w-fit rounded-full uppercase">
                                                            <CheckCircle2 size={12} /> حاضـر
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="danger" className="bg-rose-50 text-rose-600 border-none font-bold text-[9px] px-3 py-1 flex items-center gap-1.5 w-fit rounded-full uppercase">
                                                            <XCircle size={12} /> غائـب
                                                        </Badge>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-slate-50 text-[11px] font-bold text-slate-600 border border-slate-100">
                                                    {student.attendances?.length || 0}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                {isPresent ? (
                                                    <div className="inline-flex items-center justify-center gap-1.5 text-[10px] font-bold text-slate-700 bg-white px-2 py-1 rounded-lg border border-slate-100 shadow-xs tabular-nums">
                                                        <Clock size={12} className="text-primary/70" />
                                                        {new Date(lastLog.scanned_at).toLocaleTimeString('en-GB', { 
                                                            hour: '2-digit', 
                                                            minute: '2-digit',
                                                            hour12: false 
                                                        })}
                                                    </div>
                                                ) : (
                                                    <span className="text-[10px] text-slate-200 italic font-bold">---</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <button 
                                                    onClick={() => setSelectedStudentLogs(student)}
                                                    disabled={!isPresent}
                                                    className={`p-2 rounded-xl border transition-all mx-auto ${
                                                        isPresent 
                                                        ? 'bg-white border-slate-200 text-slate-400 hover:bg-slate-900 hover:text-white hover:border-transparent cursor-pointer shadow-sm' 
                                                        : 'bg-slate-50/50 text-slate-200 border-transparent cursor-not-allowed opacity-50'
                                                    }`}
                                                >
                                                    <History size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {pagination && pagination.last_page > 1 && (
                    <div className="px-6 py-5 bg-white border-t border-slate-50 flex items-center justify-between">
                         <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                            عرض <span className="text-slate-800">{pagination.from}</span> - <span className="text-slate-800">{pagination.to}</span> من أصل <span className="text-slate-800 font-bold">{pagination.total}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button 
                                variant="outline" 
                                size="sm" 
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => prev - 1)}
                                className="h-9 px-4 rounded-xl border-slate-200 font-bold text-xs"
                            >
                                السابق
                            </Button>
                            <Button 
                                variant="outline" 
                                size="sm" 
                                disabled={currentPage === pagination.last_page}
                                onClick={() => setCurrentPage(prev => prev + 1)}
                                className="h-9 px-4 rounded-xl border-slate-200 font-bold text-xs"
                            >
                                التالي
                            </Button>
                        </div>
                    </div>
                )}
            </Card>

            {/* Logs Modal Overlay */}
            <LogsModal 
                student={selectedStudentLogs} 
                onClose={() => setSelectedStudentLogs(null)} 
            />
        </div>
    );
};

export default AttendanceList;
